import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';

import {
  FaLayerGroup,
  FaFilter,
  FaSearchLocation,
  FaStar,
  FaInfoCircle,
  FaClipboard,
  FaLock,
} from 'react-icons/fa';

import {
  QUARTER_CONTRAST,
  CONTRAST,
  INFO,
  BACKGROUND_PRIMARY,
} from '../../theme';
import { LayerBuilderContainer } from '../../layer-builder';
import { Layers } from './layers';
import { Filters } from '../../filters';
import { SurveySidebar } from '../../survey';
import { enabled } from '../../feature-flags';
import { Search } from './search';
import { Information } from './information';
import { GooglePlacesSearch } from './google-places-search';

import { StarredParcels } from './starred-parcels';

const iconStyle = (isSelected) => ({
  color: isSelected ? INFO : CONTRAST,
  backgroundColor: isSelected ? BACKGROUND_PRIMARY : 'transparent',
  cursor: 'pointer',
  padding: 12,
  margin: 6,
  borderRadius: 26,
  overflow: 'visible',
});

const SideBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  top: 63px;
  left: 8px;
  z-index: 1;
`;

const TabList = styled.div`
  overflow: hidden;
  background-color: white;
  border-radius: ${({ open }) => (open ? '4px' : '32px')};
  width: ${({ open }) => (open ? '320px' : '52px')};
  height: ${
    ({ open, count }) => (open ? 'calc(100vh - 172px)' : `${55 * count}px`)
  };
  transition: height 0.2s, width 0.2s, border-radius 0.2s;
  display: flex;
  box-shadow: 1px 2px 2px ${QUARTER_CONTRAST};
  z-index: 1;
  box-sizing: border-box;


  @media (max-width: 768px) {
    max-width: 80vw;
    align-self: flex-start;
    height: ${
      ({ open, count }) => (open ? '50vh' : `${55 * count}px`)
    };
  }
`;

const Close = styled.div`
  cursor: pointer;
  position: absolute;
  cursor: pointer;
  position: absolute;
  font-size: 32px;
  line-height: 17px;
  width: 18px;
  background-color: white;
  box-shadow: 1px 2px 2px ${QUARTER_CONTRAST};
  border-radius: 16px;
  padding: 4px;
  top: -10px;
  right: -6px;
`;

const Icons = styled.div`
  width: 52px;
`;

const ContentWrapper = styled.div`
  padding-top: 16px;
  flex: 1;
`;

const Null = () => null;

export const NAV_DEFINITIONS = [
  {
    type: 'SEARCH_ADDRESS',
    name: 'Search for properties',
    Icon: FaSearchLocation,
    Content: Search,
  },
  {
    type: 'INFORMATION',
    name: 'Information',
    Icon: FaInfoCircle,
    Content: Information,
  },
  {
    type: 'GOOGLE_PLACES',
    name: 'Search Addresses',
    Icon: FaSearchLocation,
    Content: GooglePlacesSearch,
  },
  {
    type: 'STARRED_PARCELS',
    name: 'Starred Parcels',
    Icon: FaStar,
    Content: StarredParcels,
    onOpen: {
      type: 'FETCH_FAVORITES',
    },
  },
  {
    type: 'LAYER_SELECTOR',
    name: 'View map layers',
    Icon: FaLayerGroup,
    Content: Layers,
  },
  {
    type: 'FILTER',
    name: 'Filter',
    Icon: FaFilter,
    Content: Filters,
  },
  {
    type: 'LAYER_BUILDER',
    name: 'Create custom map layers',
    Icon: FaFilter,
    Content: LayerBuilderContainer,
    onOpen: {
      type: 'CREATE_NEW_EMPTY_CUSTOM_LAYERS',
    },
  },
  {
    type: 'LAYER_BUILDER_LOCKED',
    name: 'Create custom map layers',
    Icon: FaFilter,
    locked: true,
  },
  {
    type: 'SURVEYS',
    name: 'Surveys',
    Icon: FaClipboard,
    Content: SurveySidebar,
  },
];

const mapState = createSelector(
  ({ customLayersConfig }) => customLayersConfig,
  ({ sidebarFlags }) => sidebarFlags,
  ({ defaultSidebar }) => defaultSidebar,
  (customLayersConfig, sidebarFlags, defaultSidebar) => {
    const exclude = Object.keys(customLayersConfig).length
      ? ['FILTER']
      : ['LAYER_BUILDER', 'REPORT_BUILDER'];
    if (!enabled('SURVEY_TOOL')) exclude.push('SURVEYS');

    return {
      defaultSidebar,
      navDefinitions: NAV_DEFINITIONS.filter(({ type }) => (
        sidebarFlags.length ? sidebarFlags.includes(type) : true
      )).filter(({ type }) => !exclude.includes(type)),
    };
  },
);

export const SideBar = () => {
  const { navDefinitions, defaultSidebar } = useSelector(mapState);
  const [open, setOpen] = useState(defaultSidebar);
  const dispatch = useDispatch();
  const { Content } = navDefinitions.find(({ type }) => type === open) || {
    Content: Null,
  };

  return (
    <SideBarContainer>
      <TabList open={!!open} count={navDefinitions.length}>
        <Icons>
          {navDefinitions.map((def) => {
            const {
              name,
              Icon,
              type,
              onOpen,
              locked,
            } = def;

            return (
              <Tooltip
                title={name}
                aria-label={name}
                key={name}
                placement="right"
                arrow
              >
                <div
                  style={{
                    opacity: locked ? 0.5 : 1,
                    position: 'relative',
                  }}
                >
                  <Icon
                    id={name}
                    style={iconStyle(open === type)}
                    onClick={() => {
                      if (locked) return;
                      if (open !== type && onOpen) dispatch(onOpen);

                      if (open !== type) {
                        dispatch({
                          type: 'TRACK',
                          payload: {
                            name: 'Sidebar Opened',
                            page: name,
                          },
                        });
                      }

                      setOpen(open && open === type ? '' : type);
                    }}
                  />
                  {!locked ? null : (
                    <FaLock
                      style={{
                        position: 'absolute',
                        left: 33,
                        top: 21,
                        width: 8,
                        color: 'black',
                      }}
                    />
                  )}
                </div>
              </Tooltip>
            );
          })}
        </Icons>
        <ContentWrapper>
          {open ? (
            <Close onClick={() => setOpen('')}>×</Close>
          ) : null}

          {Content ? (
            <Content
              setOpenNav={(type) => {
                setOpen(type);
              }}
            />
          ) : null}
        </ContentWrapper>
      </TabList>
    </SideBarContainer>
  );
};
